import moment from "moment";
import { CSSProperties, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ILancamento } from "../../interfaces/ILancamento";
import { ICartaoDetalhe } from "../../interfaces/IRelatorio";
import { IEsteiraDetalhe } from "../../interfaces/IEsteira";
import cartaoNovaService from "../../services/cartaoNovaService";
import esteiraNovaService from "../../services/esteiraNovaService";
import lancamentoNovaService from "../../services/lancamentoNovaService";
import posicaoFinanceiraService, { IPosicaoFinanceiraPendencia, IPosicaoFinanceiraPendenciaFilter, IPosicaoFinanceiraEstatisticas } from "../../services/posicaoFinanceiraService";

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

// Azul neutro que substitui os antigos tons de verde, harmonizado com o azul
// já usado na seção Composição (#0d6efd), porém mais suave/neutro.
const NEUTRAL_BLUE = '#3f72ab';

const STATUS_LABELS: Record<string, { label: string; bg: string }> = {
    PENDENTE: { label: 'Pendente', bg: '#b5544c' },
    RECOLHIDOMAIOR: { label: 'Recolhido Maior', bg: '#dc3545' },
    RECOLHIDOMENOR: { label: 'Recolhido Menor', bg: '#fd7e14' },
    DUPLICIDADE: { label: 'Duplicidade', bg: '#6f42c1' },
    PENDENTESALDOANTERIOR: { label: 'Saldo Anterior', bg: '#6c757d' },
};

type FiltroColuna = { ano: string; mes: string; dia: string; historico: string; valor: string };
const filtroVazio: FiltroColuna = { ano: '', mes: '', dia: '', historico: '', valor: '' };

function buildFiltroApi(f: FiltroColuna) {
    const toInt = (s: string) => { const n = parseInt(s, 10); return isNaN(n) ? undefined : n; };
    const toDecimal = (s: string) => {
        const n = parseFloat(s.replace(/\./g, '').replace(',', '.'));
        return isNaN(n) ? undefined : n;
    };
    return {
        ano: f.ano ? toInt(f.ano) : undefined,
        mes: f.mes ? toInt(f.mes) : undefined,
        dia: f.dia ? toInt(f.dia) : undefined,
        historico: f.historico.trim() || undefined,
        valor: f.valor.trim() ? toDecimal(f.valor.trim()) : undefined,
    };
}

function StatusBadge({ status }: { status: string }) {
    const s = STATUS_LABELS[status] ?? { label: status, bg: '#6c757d' };
    return (
        <span style={{
            display: 'inline-block', fontSize: '10px', fontWeight: 700,
            color: '#fff', background: s.bg, borderRadius: '3px',
            padding: '1px 6px', letterSpacing: '0.4px', textTransform: 'uppercase',
            whiteSpace: 'nowrap',
        }}>{s.label}</span>
    );
}

function CardLancamento({ lancamento, tipo }: { lancamento: ILancamento; tipo: string }) {
    const isCredito = tipo === 'C';
    const accentColor = isCredito ? NEUTRAL_BLUE : '#dc3545';
    const valor = lancamento.valorProvisaoRecolher ?? 0;

    return (
        <div style={{
            background: '#fff',
            borderRadius: '4px',
            padding: '10px 14px',
            marginBottom: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
            border: '1px solid #e9ecef',
            borderLeftColor: accentColor,
            borderLeftWidth: '3px',
            borderLeftStyle: 'solid',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '8px' }}>
                <span style={{ fontWeight: 700, fontSize: '14px', color: accentColor, whiteSpace: 'nowrap' }}>
                    {BRL.format(valor)}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <StatusBadge status={lancamento.status} />
                    <span style={{
                        display: 'inline-block', fontSize: '10px', fontWeight: 700,
                        color: isCredito ? '#0f5132' : '#842029',
                        background: isCredito ? '#d1e7dd' : '#f8d7da',
                        borderRadius: '3px', padding: '1px 6px',
                    }}>{tipo}</span>
                    <span style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                        {lancamento.dataLancamento ? moment(lancamento.dataLancamento).format('DD/MM/YYYY') : '—'}
                    </span>
                </div>
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: '#495057', lineHeight: '1.4', wordBreak: 'break-word' }}>
                {lancamento.historico || '—'}
            </p>
            {lancamento.codigoContabil && (
                <span style={{ fontSize: '10px', color: '#6c757d', marginTop: '4px', display: 'block' }}>
                    Conta: <strong>{lancamento.codigoContabil}</strong>
                </span>
            )}
        </div>
    );
}

function FiltroBox({
    filtros, color, btnClass, loading,
    onChange, onBuscar, onLimpar,
}: {
    filtros: FiltroColuna;
    color: string;
    btnClass: string;
    loading: boolean;
    onChange: (f: FiltroColuna) => void;
    onBuscar: () => void;
    onLimpar: () => void;
}) {
    const set = (key: keyof FiltroColuna, value: string) =>
        onChange({ ...filtros, [key]: value });

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !loading) onBuscar();
    };

    return (
        <div style={{ border: `1px solid ${color}`, borderTop: 'none', borderBottom: 'none', padding: '8px 10px', background: '#fafafa' }}>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '5px', flexWrap: 'wrap' }}>
                <input
                    className="form-control form-control-sm"
                    type="number" placeholder="Ano"
                    value={filtros.ano}
                    onChange={e => set('ano', e.target.value)}
                    onKeyDown={onKeyDown}
                    style={{ width: '72px' }}
                />
                <input
                    className="form-control form-control-sm"
                    type="number" placeholder="Mês" min={1} max={12}
                    value={filtros.mes}
                    onChange={e => set('mes', e.target.value)}
                    onKeyDown={onKeyDown}
                    style={{ width: '62px' }}
                />
                <input
                    className="form-control form-control-sm"
                    type="number" placeholder="Dia" min={1} max={31}
                    value={filtros.dia}
                    onChange={e => set('dia', e.target.value)}
                    onKeyDown={onKeyDown}
                    style={{ width: '56px' }}
                />
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <input
                    className="form-control form-control-sm"
                    type="text" placeholder="Histórico..."
                    value={filtros.historico}
                    onChange={e => set('historico', e.target.value)}
                    onKeyDown={onKeyDown}
                    style={{ flex: 1, minWidth: '120px' }}
                />
                <input
                    className="form-control form-control-sm"
                    type="text" placeholder="Valor"
                    value={filtros.valor}
                    onChange={e => set('valor', e.target.value)}
                    onKeyDown={onKeyDown}
                    style={{ width: '80px' }}
                />
                <button
                    className={`btn btn-sm ${btnClass}`}
                    onClick={onBuscar}
                    disabled={loading}
                    style={{ padding: '2px 12px', fontSize: '12px', whiteSpace: 'nowrap', minWidth: '72px' }}
                >
                    {loading
                        ? <span className="spinner-border spinner-border-sm" role="status" />
                        : 'Buscar'}
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={onLimpar} disabled={loading} style={{ padding: '2px 10px', fontSize: '12px' }}>
                    ×
                </button>
            </div>
        </div>
    );
}

const PER_PAGE_OPTIONS = [50, 100, 150, 200];

function Paginacao({ page, totalPages, total, perPage, loading, onPrev, onNext, onPerPageChange }: {
    page: number; totalPages: number; total: number; perPage: number;
    loading: boolean; onPrev: () => void; onNext: () => void;
    onPerPageChange: (n: number) => void;
}) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 4px', borderTop: '1px solid #e9ecef', marginTop: '8px',
            flexWrap: 'wrap', gap: '6px',
        }}>
            <button onClick={onPrev} disabled={page <= 1 || loading}
                className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>
                ‹ Ant.
            </button>
            <span style={{ fontSize: '12px', color: '#6c757d' }}>
                {loading
                    ? <span className="spinner-border spinner-border-sm" role="status" />
                    : `Pág. ${page} / ${totalPages} · ${total} lançamento${total !== 1 ? 's' : ''}`}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap' }}>Por página:</span>
                {PER_PAGE_OPTIONS.map(n => (
                    <button
                        key={n}
                        onClick={() => onPerPageChange(n)}
                        disabled={loading}
                        className={`btn btn-sm ${perPage === n ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        style={{ padding: '1px 8px', fontSize: '11px' }}
                    >
                        {n}
                    </button>
                ))}
            </div>
            <button onClick={onNext} disabled={page >= totalPages || loading}
                className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>
                Próx. ›
            </button>
        </div>
    );
}

function SectionHeader({ label: title, count, color }: { label: string; count?: number; color: string }) {
    const style: CSSProperties = {
        background: color, color: '#fff',
        borderRadius: '6px 6px 0 0',
        padding: '10px 16px',
        fontWeight: 700, fontSize: '14px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        letterSpacing: '0.3px',
    };
    return (
        <div style={style}>
            <span>{title}</span>
            {count !== undefined && (
                <span style={{
                    background: 'rgba(255,255,255,0.25)', borderRadius: '12px',
                    padding: '2px 12px', fontSize: '12px', fontWeight: 600,
                }}>{count}</span>
            )}
        </div>
    );
}

type UrlParam = { idCartao: string };

function CapaConciliacao() {
    const { idCartao } = useParams<UrlParam>();
    const cartaoId = parseInt(idCartao, 10);

    const [cartao, setCartao] = useState<ICartaoDetalhe>();
    const [esteira, setEsteira] = useState<IEsteiraDetalhe>();
    const [iniciado, setIniciado] = useState(false);

    const [ajustes, setAjustes] = useState<ILancamento[]>([]);
    const [loadingAjustes, setLoadingAjustes] = useState(false);
    const [pageAjustes, setPageAjustes] = useState(1);
    const [totalPagesAjustes, setTotalPagesAjustes] = useState(1);
    const [totalAjustes, setTotalAjustes] = useState(0);
    const [filtrosAjustes, setFiltrosAjustes] = useState<FiltroColuna>(filtroVazio);
    const [perPageAjustes, setPerPageAjustes] = useState(50);

    const [composicao, setComposicao] = useState<ILancamento[]>([]);
    const [loadingComposicao, setLoadingComposicao] = useState(false);
    const [pageComposicao, setPageComposicao] = useState(1);
    const [totalPagesComposicao, setTotalPagesComposicao] = useState(1);
    const [totalComposicao, setTotalComposicao] = useState(0);
    const [filtrosComposicao, setFiltrosComposicao] = useState<FiltroColuna>(filtroVazio);
    const [perPageComposicao, setPerPageComposicao] = useState(50);

    const [somaAno, setSomaAno] = useState<{ somaCreditos: number; somaDebitos: number }>();
    const [ageSaldo, setAgeSaldo] = useState<{ totalGeral: number; faixas: { faixa: string; total: number }[] } | null>(null);

    const [erroSomaAno, setErroSomaAno] = useState(false);

    const carregarAjustes = useCallback((page: number, esteiraId: number, tipo: 'C' | 'D', filtros?: FiltroColuna, perPage?: number) => {
        setLoadingAjustes(true);
        return lancamentoNovaService.listarPendentes(esteiraId, cartaoId, page, perPage ?? 50, tipo, buildFiltroApi(filtros ?? filtroVazio))
            .then((resp) => {
                setAjustes(resp.items);
                setTotalPagesAjustes(resp.totalPages);
                setTotalAjustes(resp.total);
                setPageAjustes(page);
            })
            .catch(() => { /* mantém a última lista carregada; spinner encerra no finally */ })
            .finally(() => setLoadingAjustes(false));
    }, [cartaoId]);

    const carregarComposicao = useCallback((page: number, esteiraId: number, tipo: 'C' | 'D', filtros?: FiltroColuna, perPage?: number) => {
        setLoadingComposicao(true);
        return lancamentoNovaService.listarPendentes(esteiraId, cartaoId, page, perPage ?? 50, tipo, buildFiltroApi(filtros ?? filtroVazio))
            .then((resp) => {
                setComposicao(resp.items);
                setTotalPagesComposicao(resp.totalPages);
                setTotalComposicao(resp.total);
                setPageComposicao(page);
            })
            .catch(() => { /* mantém a última lista carregada; spinner encerra no finally */ })
            .finally(() => setLoadingComposicao(false));
    }, [cartaoId]);

    useEffect(() => {
        if (iniciado) return;
        setIniciado(true);
        cartaoNovaService.buscarCartaoPorId(cartaoId).then((c) => {
            setCartao(c);
            const isAtivoLocal = c.contaReferencial?.startsWith('1.');

            // AGE é a consulta mais pesada da tela (soma toda a conta) — só dispara
            // depois que o essencial (saldo, ajustes, composição) já carregou, pra
            // não competir por conexão/tempo com o que o usuário vê primeiro.
            const essenciais = Promise.allSettled([
                esteiraNovaService.buscarDetalhe(c.esteiraID).then((e) => {
                    setEsteira(e);
                    return lancamentoNovaService.buscarSomaAno(c.esteiraID, cartaoId)
                        .then(setSomaAno)
                        .catch(() => setErroSomaAno(true));
                }),
                carregarAjustes(1, c.esteiraID, isAtivoLocal ? 'C' : 'D', filtroVazio, 50),
                carregarComposicao(1, c.esteiraID, isAtivoLocal ? 'D' : 'C', filtroVazio, 50),
            ]);

            if (c.isClienteFornecedor) {
                essenciais.then(() => {
                    lancamentoNovaService.buscarAge(c.esteiraID, cartaoId)
                        .then(setAgeSaldo)
                        .catch(() => setAgeSaldo({ totalGeral: 0, faixas: [] }));
                });
            }
        });
    }, [iniciado, cartaoId, carregarAjustes, carregarComposicao]);

    if (!cartao || !esteira) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh', gap: '16px' }}>
                <div className="spinner-border text-primary" style={{ width: '2.5rem', height: '2.5rem' }} role="status" />
                <span className="text-muted fs-6">Carregando dados da conta...</span>
            </div>
        );
    }

    const isAtivo = cartao.contaReferencial?.startsWith('1.');
    const tipoAjuste: 'C' | 'D' = isAtivo ? 'C' : 'D';
    const tipoComposicao: 'C' | 'D' = isAtivo ? 'D' : 'C';

    const tipoSaldoInicial = cartao.lancamentoInicial || (cartao.saldoInicial >= 0 ? 'C' : 'D');
    const tipoSaldoFinal = cartao.lancamento || (cartao.saldoFinalReal >= 0 ? 'C' : 'D');

    const tipoBadge = (tipo: string) => (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: '4px',
            fontWeight: 700, fontSize: '13px',
            background: tipo === 'C' ? '#d1e7dd' : '#f8d7da',
            color: tipo === 'C' ? '#0f5132' : '#842029',
        }}>{tipo}</span>
    );

    const sectionBodyStyle = (color: string): CSSProperties => ({
        border: `1px solid ${color}`,
        borderTop: 'none',
        borderRadius: '0 0 6px 6px',
        padding: '12px',
        minHeight: '60px',
        background: '#fff',
    });

    const colorAjuste = tipoAjuste === 'C' ? NEUTRAL_BLUE : '#6c757d';
    const btnAjuste = tipoAjuste === 'C' ? 'btn-primary' : 'btn-secondary';
    const colorComposicao = '#0d6efd';

    const loadingPlaceholder = (color: string) => (
        <div className="d-flex justify-content-center align-items-center" style={{ padding: '24px', gap: '12px' }}>
            <div className="spinner-border" style={{ color }} role="status" />
            <span className="text-muted">Carregando lançamentos...</span>
        </div>
    );

    return (
        <div style={{ padding: '20px 16px' }}>

            {/* Barra de ações */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button
                    className="btn btn-danger"
                    disabled
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, opacity: 0.65, cursor: 'not-allowed' }}
                >
                    <i className="fa fa-file-pdf" />
                    Obter PDF
                </button>
            </div>

            {/* Cabeçalho da conta */}
            <div style={{ borderRadius: '8px', border: '1px solid #dee2e6', marginBottom: '24px', overflow: 'hidden' }}>
                <div style={{ background: '#343a40', color: '#fff', padding: '12px 20px', fontWeight: 700, fontSize: '15px' }}>
                    Capa de Conciliação
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', background: '#f8f9fa' }}>
                    {[
                        { label: 'Conta', value: `${cartao.contaEmpresa}${cartao.descricaoEmpresa ? ` — ${cartao.descricaoEmpresa}` : ''}` },
                        { label: 'Conta Referencial', value: `${cartao.contaReferencial}${cartao.descricaoReferencial ? ` — ${cartao.descricaoReferencial}` : ''}` },
                        { label: 'Período', value: `${esteira.periodoInicioString} – ${esteira.periodoFimString}` },
                        { label: 'Tipo', value: isAtivo ? 'Ativo' : 'Passivo / Resultado' },
                    ].map((item, i) => (
                        <div key={i} style={{ padding: '12px 20px', borderRight: '1px solid #dee2e6', flex: '1 1 auto' }}>
                            <div style={{ fontSize: '10px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: '3px' }}>
                                {item.label}
                            </div>
                            <div style={{ fontWeight: 600, fontSize: '13px', color: '#212529' }}>{item.value}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Demonstrativo do Saldo */}
            <div style={{ marginBottom: '24px' }}>
                <SectionHeader label="DEMONSTRATIVO DO SALDO" color={NEUTRAL_BLUE} />
                <div style={{ border: `1px solid ${NEUTRAL_BLUE}`, borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
                    {([
                        {
                            label: 'Saldo Inicial',
                            valor: cartao.saldoInicial,
                            tipo: tipoSaldoInicial,
                            soma: null as number | null,
                        },
                        {
                            label: `Ajustes e Pendências (${tipoAjuste === 'C' ? 'Créditos' : 'Débitos'})`,
                            valor: null as number | null,
                            tipo: tipoAjuste,
                            soma: somaAno ? (tipoAjuste === 'C' ? somaAno.somaCreditos : somaAno.somaDebitos) : null,
                        },
                        {
                            label: `Composição (${tipoComposicao === 'D' ? 'Débitos' : 'Créditos'})`,
                            valor: null as number | null,
                            tipo: tipoComposicao,
                            soma: somaAno ? (tipoComposicao === 'D' ? somaAno.somaDebitos : somaAno.somaCreditos) : null,
                        },
                        {
                            label: 'Saldo Final',
                            valor: cartao.saldoFinalReal,
                            tipo: tipoSaldoFinal,
                            soma: null as number | null,
                        },
                    ] as { label: string; valor: number | null; tipo: string; soma: number | null }[]).map((row, i, arr) => (
                        <div key={i} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '12px 20px',
                            background: i % 2 === 0 ? '#f8f9fa' : '#fff',
                            borderBottom: i < arr.length - 1 ? '1px solid #e9ecef' : 'none',
                        }}>
                            <span style={{ fontWeight: 600, color: '#212529', fontSize: '14px' }}>{row.label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {row.soma !== null ? (
                                    <span style={{ fontWeight: 700, fontSize: '16px', color: '#212529' }}>
                                        {BRL.format(row.soma)}
                                    </span>
                                ) : row.valor !== null ? (
                                    <span style={{ fontWeight: 700, fontSize: '16px', color: '#212529' }}>
                                        {BRL.format(Math.abs(row.valor))}
                                    </span>
                                ) : erroSomaAno ? (
                                    <span style={{ fontSize: '12px', color: '#dc3545', fontWeight: 600 }} title="Falha ao carregar o total do ano">
                                        Erro ao carregar
                                    </span>
                                ) : (
                                    <span className="spinner-border spinner-border-sm" style={{ color: NEUTRAL_BLUE }} role="status" />
                                )}
                                {tipoBadge(row.tipo)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Seção 1: Ajustes e Pendências */}
            <div style={{ marginBottom: '24px' }}>
                <SectionHeader
                    label={`AJUSTES E PENDÊNCIAS (${tipoAjuste === 'C' ? 'CRÉDITOS' : 'DÉBITOS'})`}
                    count={totalAjustes}
                    color={colorAjuste}
                />
                <div style={{ border: `1px solid ${colorAjuste}`, borderTop: 'none', borderBottom: 'none', padding: '6px 16px', background: '#eef3f8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
                        Total ano vigente:
                    </span>
                    {somaAno
                        ? <span style={{ fontWeight: 700, fontSize: '14px', color: colorAjuste }}>
                            {BRL.format(tipoAjuste === 'C' ? somaAno.somaCreditos : somaAno.somaDebitos)}
                          </span>
                        : erroSomaAno
                            ? <span style={{ fontSize: '12px', color: '#dc3545', fontWeight: 600 }}>Erro ao carregar</span>
                            : <span className="spinner-border spinner-border-sm" style={{ color: colorAjuste }} role="status" />
                    }
                </div>
                <FiltroBox
                    filtros={filtrosAjustes}
                    color={colorAjuste}
                    btnClass={btnAjuste}
                    loading={loadingAjustes}
                    onChange={setFiltrosAjustes}
                    onBuscar={() => carregarAjustes(1, cartao.esteiraID, tipoAjuste, filtrosAjustes)}
                    onLimpar={() => {
                        setFiltrosAjustes(filtroVazio);
                        carregarAjustes(1, cartao.esteiraID, tipoAjuste, filtroVazio);
                    }}
                />
                <div style={sectionBodyStyle(colorAjuste)}>
                    {loadingAjustes && ajustes.length === 0
                        ? loadingPlaceholder(colorAjuste)
                        : ajustes.length === 0
                            ? <p className="text-center text-muted" style={{ fontSize: '13px', margin: '16px 0' }}>Nenhum lançamento encontrado.</p>
                            : ajustes.map((item) => <CardLancamento key={item.id} lancamento={item} tipo={tipoAjuste} />)
                    }
                    <Paginacao
                        page={pageAjustes} totalPages={totalPagesAjustes} total={totalAjustes}
                        perPage={perPageAjustes} loading={loadingAjustes}
                        onPrev={() => carregarAjustes(pageAjustes - 1, cartao.esteiraID, tipoAjuste, filtrosAjustes, perPageAjustes)}
                        onNext={() => carregarAjustes(pageAjustes + 1, cartao.esteiraID, tipoAjuste, filtrosAjustes, perPageAjustes)}
                        onPerPageChange={(n) => { setPerPageAjustes(n); carregarAjustes(1, cartao.esteiraID, tipoAjuste, filtrosAjustes, n); }}
                    />
                </div>
            </div>

            {/* Seção 2: Composição */}
            <div style={{ marginBottom: '32px' }}>
                <SectionHeader
                    label={`COMPOSIÇÃO (${tipoComposicao === 'D' ? 'DÉBITOS' : 'CRÉDITOS'})`}
                    count={totalComposicao}
                    color={colorComposicao}
                />
                <div style={{ border: `1px solid ${colorComposicao}`, borderTop: 'none', borderBottom: 'none', padding: '6px 16px', background: '#f0f4ff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
                        Total ano vigente:
                    </span>
                    {somaAno
                        ? <span style={{ fontWeight: 700, fontSize: '14px', color: colorComposicao }}>
                            {BRL.format(tipoComposicao === 'D' ? somaAno.somaDebitos : somaAno.somaCreditos)}
                          </span>
                        : erroSomaAno
                            ? <span style={{ fontSize: '12px', color: '#dc3545', fontWeight: 600 }}>Erro ao carregar</span>
                            : <span className="spinner-border spinner-border-sm" style={{ color: colorComposicao }} role="status" />
                    }
                </div>
                <FiltroBox
                    filtros={filtrosComposicao}
                    color={colorComposicao}
                    btnClass="btn-primary"
                    loading={loadingComposicao}
                    onChange={setFiltrosComposicao}
                    onBuscar={() => carregarComposicao(1, cartao.esteiraID, tipoComposicao, filtrosComposicao)}
                    onLimpar={() => {
                        setFiltrosComposicao(filtroVazio);
                        carregarComposicao(1, cartao.esteiraID, tipoComposicao, filtroVazio);
                    }}
                />
                <div style={sectionBodyStyle(colorComposicao)}>
                    {loadingComposicao && composicao.length === 0
                        ? loadingPlaceholder(colorComposicao)
                        : composicao.length === 0
                            ? <p className="text-center text-muted" style={{ fontSize: '13px', margin: '16px 0' }}>Nenhum lançamento encontrado.</p>
                            : composicao.map((item) => <CardLancamento key={item.id} lancamento={item} tipo={tipoComposicao} />)
                    }
                    <Paginacao
                        page={pageComposicao} totalPages={totalPagesComposicao} total={totalComposicao}
                        perPage={perPageComposicao} loading={loadingComposicao}
                        onPrev={() => carregarComposicao(pageComposicao - 1, cartao.esteiraID, tipoComposicao, filtrosComposicao, perPageComposicao)}
                        onNext={() => carregarComposicao(pageComposicao + 1, cartao.esteiraID, tipoComposicao, filtrosComposicao, perPageComposicao)}
                        onPerPageChange={(n) => { setPerPageComposicao(n); carregarComposicao(1, cartao.esteiraID, tipoComposicao, filtrosComposicao, n); }}
                    />
                </div>
            </div>

            {/* Seção 3: Posição Financeira */}
            <PosicaoFinanceiraSection esteiraId={cartao.esteiraID} contaContabil={cartao.contaEmpresa} />

            {/* Seção 4: Estatísticas de Conciliação */}
            <PosicaoFinanceiraStatsSection esteiraId={cartao.esteiraID} contaContabil={cartao.contaEmpresa} />

            {/* Seção 5: AGE da Conta — apenas para Clientes/Fornecedores */}
            {cartao.isClienteFornecedor && <AgeSaldoSection age={ageSaldo} />}

        </div>
    );
}

// ─── Posição Financeira ───────────────────────────────────────────────────────

const filtroVazioPos: IPosicaoFinanceiraPendenciaFilter = {
    somentePendentes: true, tipo: '', razaoSocial: '', numeroDocumento: '',
    contaContabil: '', dataProvisaoInicio: '', dataProvisaoFim: '',
};

function CheckIcon({ found, ids, onClick }: { found: boolean; ids: number[]; onClick: (ids: number[]) => void }) {
    if (found && ids.length > 0) {
        return (
            <button onClick={() => onClick(ids)} className="btn btn-sm btn-link p-0"
                title={ids.length > 1 ? `Ver detalhes dos ${ids.length} lançamentos` : "Ver detalhes do lançamento"}
                style={{ color: '#198754', fontWeight: 700, fontSize: '15px', lineHeight: 1 }}>
                ✔{ids.length > 1 && <sup style={{ fontSize: '9px', marginLeft: '1px' }}>{ids.length}</sup>}
            </button>
        );
    }
    return <span style={{ color: '#dc3545', fontWeight: 700, fontSize: '15px' }}>✘</span>;
}

function LancamentoDetalheModal({ ids, onClose }: { ids: number[]; onClose: () => void }) {
    const [lancamentos, setLancamentos] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all(ids.map(id => lancamentoNovaService.buscarPorId(id).catch(() => null)))
            .then(setLancamentos)
            .finally(() => setLoading(false));
    }, [ids]);

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onClose}>
            <div style={{
                background: '#fff', borderRadius: '8px', padding: '24px', minWidth: '340px', maxWidth: '520px',
                maxHeight: '80vh', overflowY: 'auto',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)', position: 'relative',
            }} onClick={e => e.stopPropagation()}>
                <button onClick={onClose} style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6c757d' }}>×</button>
                <h6 style={{ fontWeight: 700, marginBottom: '16px', color: '#343a40' }}>
                    {ids.length > 1 ? `Detalhes dos Lançamentos (${ids.length})` : 'Detalhes do Lançamento'}
                </h6>
                {loading ? (
                    <div className="d-flex justify-content-center py-3">
                        <div className="spinner-border text-primary" role="status" />
                    </div>
                ) : lancamentos.every(l => !l) ? (
                    <p className="text-muted text-center">Nenhum lançamento encontrado.</p>
                ) : (
                    lancamentos.map((lancamento, i) => (
                        <div key={ids[i] ?? i} style={{
                            marginBottom: i < lancamentos.length - 1 ? '14px' : 0,
                            paddingBottom: i < lancamentos.length - 1 ? '14px' : 0,
                            borderBottom: i < lancamentos.length - 1 ? '1px solid #e9ecef' : 'none',
                        }}>
                            {ids.length > 1 && (
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#6f42c1', marginBottom: '4px' }}>
                                    Lançamento #{ids[i]}
                                </div>
                            )}
                            {!lancamento ? (
                                <p className="text-muted mb-0">Lançamento não encontrado.</p>
                            ) : (
                                <table className="table table-sm table-borderless mb-0">
                                    <tbody>
                                        {[
                                            { label: 'Histórico', val: lancamento.historico },
                                            { label: 'Valor', val: BRL.format(Math.abs(lancamento.valorProvisaoRecolher ?? 0)) },
                                            { label: 'Tipo', val: lancamento.tipoLancamento },
                                            { label: 'Data', val: lancamento.dataLancamento ? moment(lancamento.dataLancamento).format('DD/MM/YYYY') : '—' },
                                            { label: 'Status', val: lancamento.status },
                                            { label: 'Conta', val: lancamento.codigoContabil },
                                        ].map((r, j) => (
                                            <tr key={j}>
                                                <td style={{ color: '#6c757d', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', paddingRight: '12px' }}>{r.label}</td>
                                                <td style={{ fontSize: '13px', color: '#212529' }}>{r.val || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

function PosicaoFinanceiraSection({ esteiraId, contaContabil }: { esteiraId: number; contaContabil: string }) {
    const [items, setItems] = useState<IPosicaoFinanceiraPendencia[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(20);
    const [filtros, setFiltros] = useState<IPosicaoFinanceiraPendenciaFilter>(filtroVazioPos);
    const [modalLancamentoIds, setModalLancamentoIds] = useState<number[] | null>(null);
    const [iniciado, setIniciado] = useState(false);

    const carregar = useCallback((pg: number, pp: number, f: IPosicaoFinanceiraPendenciaFilter) => {
        setLoading(true);
        posicaoFinanceiraService.listarPendencias(esteiraId, { ...f, page: pg, perPage: pp, contaContabilConciliacao: contaContabil })
            .then(resp => {
                setItems(resp.items);
                setTotalPages(resp.totalPages);
                setTotal(resp.total);
                setPage(pg);
            })
            .finally(() => setLoading(false));
    }, [esteiraId]);

    useEffect(() => {
        if (iniciado) return;
        setIniciado(true);
        carregar(1, 20, filtroVazioPos);
    }, [iniciado, carregar]);

    const setF = (key: keyof IPosicaoFinanceiraPendenciaFilter, value: any) =>
        setFiltros(prev => ({ ...prev, [key]: value }));

    const onBuscar = () => carregar(1, perPage, filtros);
    const onLimpar = () => { setFiltros(filtroVazioPos); carregar(1, perPage, filtroVazioPos); };

    const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && !loading) onBuscar(); };

    const color = '#6f42c1';

    return (
        <>
            {modalLancamentoIds && (
                <LancamentoDetalheModal ids={modalLancamentoIds} onClose={() => setModalLancamentoIds(null)} />
            )}

            <div style={{ marginBottom: '32px' }}>
                {/* Header */}
                <div style={{
                    background: color, color: '#fff', borderRadius: '6px 6px 0 0',
                    padding: '10px 16px', fontWeight: 700, fontSize: '14px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <span>POSIÇÃO FINANCEIRA — PENDÊNCIAS</span>
                    <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '12px', padding: '2px 12px', fontSize: '12px', fontWeight: 600 }}>{total}</span>
                </div>

                {/* Filtros */}
                <div style={{ border: `1px solid ${color}`, borderTop: 'none', borderBottom: 'none', padding: '8px 10px', background: '#fafafa' }}>
                    {/* Linha 1: datas e checkboxes */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '5px', alignItems: 'flex-end' }}>
                        <div>
                            <div style={{ fontSize: '10px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>Provisão — início</div>
                            <input className="form-control form-control-sm" type="date"
                                value={filtros.dataProvisaoInicio ?? ''} onChange={e => setF('dataProvisaoInicio', e.target.value)}
                                onKeyDown={onKeyDown} style={{ width: '140px' }} />
                        </div>
                        <div>
                            <div style={{ fontSize: '10px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '2px' }}>Provisão — fim</div>
                            <input className="form-control form-control-sm" type="date"
                                value={filtros.dataProvisaoFim ?? ''} onChange={e => setF('dataProvisaoFim', e.target.value)}
                                onKeyDown={onKeyDown} style={{ width: '140px' }} />
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            <input type="checkbox" checked={filtros.somentePendentes ?? true}
                                onChange={e => setF('somentePendentes', e.target.checked)} />
                            Só pendências
                        </label>
                        <select className="form-select form-select-sm" style={{ width: '130px' }}
                            value={filtros.encontrouProvisao === undefined ? '' : String(filtros.encontrouProvisao)}
                            onChange={e => setF('encontrouProvisao', e.target.value === '' ? undefined : e.target.value === 'true')}>
                            <option value="">Provisão: todos</option>
                            <option value="true">Provisão: encontrada</option>
                            <option value="false">Provisão: não encontrada</option>
                        </select>
                    </div>
                    {/* Linha 2: texto */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <input className="form-control form-control-sm" type="text" placeholder="Razão Social..."
                            value={filtros.razaoSocial ?? ''} onChange={e => setF('razaoSocial', e.target.value)}
                            onKeyDown={onKeyDown} style={{ flex: 2, minWidth: '120px' }} />
                        <input className="form-control form-control-sm" type="text" placeholder="Nº Documento"
                            value={filtros.numeroDocumento ?? ''} onChange={e => setF('numeroDocumento', e.target.value)}
                            onKeyDown={onKeyDown} style={{ width: '120px' }} />
                        <input className="form-control form-control-sm" type="text" placeholder="Tipo"
                            value={filtros.tipo ?? ''} onChange={e => setF('tipo', e.target.value)}
                            onKeyDown={onKeyDown} style={{ width: '90px' }} />
                        <input className="form-control form-control-sm" type="text" placeholder="Conta contábil"
                            value={filtros.contaContabil ?? ''} onChange={e => setF('contaContabil', e.target.value)}
                            onKeyDown={onKeyDown} style={{ width: '120px' }} />
                        <button className="btn btn-sm btn-outline-secondary" style={{ background: color, color: '#fff', border: 'none', padding: '2px 12px', fontSize: '12px', minWidth: '72px' }}
                            onClick={onBuscar} disabled={loading}>
                            {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Buscar'}
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={onLimpar} disabled={loading}
                            style={{ padding: '2px 10px', fontSize: '12px' }}>×</button>
                    </div>
                </div>

                {/* Tabela */}
                <div style={{ border: `1px solid ${color}`, borderTop: 'none', borderRadius: '0 0 6px 6px', overflowX: 'auto', background: '#fff' }}>
                    {loading && items.length === 0 ? (
                        <div className="d-flex justify-content-center align-items-center py-4" style={{ gap: '12px' }}>
                            <div className="spinner-border" style={{ color }} role="status" />
                            <span className="text-muted">Carregando...</span>
                        </div>
                    ) : items.length === 0 ? (
                        <p className="text-center text-muted py-3" style={{ fontSize: '13px' }}>Nenhum registro encontrado.</p>
                    ) : (
                        <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px', minWidth: '820px' }}>
                            <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0 }}>
                                <tr>
                                    <th style={{ padding: '8px 10px' }}>Razão Social</th>
                                    <th style={{ whiteSpace: 'nowrap', padding: '8px 10px' }}>Data Provisão</th>
                                    <th style={{ whiteSpace: 'nowrap', padding: '8px 10px', textAlign: 'right' }}>Valor Provisão</th>
                                    <th style={{ padding: '8px 10px' }}>Nº Documento</th>
                                    <th style={{ textAlign: 'center', padding: '8px 10px' }}>Provisão</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr key={item.id} style={{ opacity: loading ? 0.6 : 1 }}>
                                        <td style={{ padding: '7px 10px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {item.razaoSocial || '—'}
                                        </td>
                                        <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>
                                            {item.dataProvisao ? moment(item.dataProvisao).format('DD/MM/YYYY') : '—'}
                                        </td>
                                        <td style={{ padding: '7px 10px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                            {item.valorProvisao != null ? BRL.format(item.valorProvisao) : '—'}
                                        </td>
                                        <td style={{ padding: '7px 10px', color: '#6c757d' }}>
                                            {item.numeroDocumento || '—'}
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '7px 10px' }}>
                                            <CheckIcon found={item.encontrouProvisao} ids={item.lancamentoIds}
                                                onClick={setModalLancamentoIds} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Paginação */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid #e9ecef', flexWrap: 'wrap', gap: '6px' }}>
                        <button onClick={() => carregar(page - 1, perPage, filtros)} disabled={page <= 1 || loading}
                            className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>‹ Ant.</button>
                        <span style={{ fontSize: '12px', color: '#6c757d' }}>
                            {loading
                                ? <span className="spinner-border spinner-border-sm" role="status" />
                                : `Pág. ${page} / ${totalPages} · ${total} registro${total !== 1 ? 's' : ''}`}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap' }}>Por página:</span>
                            {[20, 50, 100].map(n => (
                                <button key={n} onClick={() => { setPerPage(n); carregar(1, n, filtros); }}
                                    disabled={loading}
                                    className={`btn btn-sm ${perPage === n ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                    style={{ padding: '1px 8px', fontSize: '11px' }}>{n}</button>
                            ))}
                        </div>
                        <button onClick={() => carregar(page + 1, perPage, filtros)} disabled={page >= totalPages || loading}
                            className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>Próx. ›</button>
                    </div>
                </div>
            </div>
        </>
    );
}

// ─── Estatísticas de Conciliação ─────────────────────────────────────────────

function BarPercent({ value, color }: { value: number; color: string }) {
    const w = Math.min(100, Math.max(0, value));
    return (
        <div style={{ background: '#e9ecef', borderRadius: '4px', height: '10px', overflow: 'hidden', flex: 1 }}>
            <div style={{ width: `${w}%`, height: '100%', background: color, borderRadius: '4px', transition: 'width 0.5s ease' }} />
        </div>
    );
}

function PosicaoFinanceiraStatsSection({ esteiraId, contaContabil }: { esteiraId: number; contaContabil: string }) {
    const [stats, setStats] = useState<IPosicaoFinanceiraEstatisticas | null>(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(false);

    useEffect(() => {
        posicaoFinanceiraService.buscarEstatisticas(esteiraId, contaContabil)
            .then(setStats)
            .catch(() => setErro(true))
            .finally(() => setLoading(false));
    }, [esteiraId, contaContabil]);

    const color = '#6f42c1';

    return (
        <div style={{ marginBottom: '32px' }}>
            <div style={{
                background: color, color: '#fff', borderRadius: '6px 6px 0 0',
                padding: '10px 16px', fontWeight: 700, fontSize: '14px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
                <span>CONCILIAÇÃO DA POSIÇÃO FINANCEIRA</span>
                {!loading && stats && (
                    <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '12px', padding: '2px 12px', fontSize: '12px', fontWeight: 600 }}>
                        {stats.totalAtivos} registro{stats.totalAtivos !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            <div style={{ border: `1px solid ${color}`, borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#fff', padding: '16px 20px' }}>
                {loading ? (
                    <div className="d-flex align-items-center justify-content-center py-3" style={{ gap: '10px' }}>
                        <span className="spinner-border spinner-border-sm" style={{ color }} role="status" />
                        <span className="text-muted" style={{ fontSize: '13px' }}>Calculando...</span>
                    </div>
                ) : erro || !stats ? (
                    <p className="text-muted text-center py-2" style={{ fontSize: '13px' }}>Não foi possível carregar as estatísticas.</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                        {/* Resumo de contagens */}
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {[
                                { label: 'Total Ativo', value: stats.totalAtivos, bg: '#f3f0f8', textColor: color },
                                { label: 'Pendentes', value: stats.totalPendentes, bg: '#fff8e1', textColor: '#856404' },
                                { label: 'Conciliados na Conta', value: stats.conciliadosNaConta, bg: '#d1e7dd', textColor: '#0f5132' },
                                { label: 'Conciliados no Geral', value: stats.conciliadosGeral, bg: '#cfe2ff', textColor: '#084298' },
                            ].map(item => (
                                <div key={item.label} style={{
                                    background: item.bg, borderRadius: '6px', padding: '10px 16px',
                                    flex: '1 1 120px', textAlign: 'center',
                                }}>
                                    <div style={{ fontSize: '22px', fontWeight: 700, color: item.textColor, lineHeight: 1.2 }}>
                                        {item.value}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '3px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                        {item.label}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Barras de percentual */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '12px', color: '#495057', fontWeight: 600, whiteSpace: 'nowrap', minWidth: '200px' }}>
                                    % Conciliado nesta conta
                                </span>
                                <BarPercent value={Number(stats.porcentagemConta)} color="#198754" />
                                <span style={{ fontSize: '14px', fontWeight: 700, color: '#198754', minWidth: '50px', textAlign: 'right' }}>
                                    {Number(stats.porcentagemConta).toFixed(1)}%
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '12px', color: '#495057', fontWeight: 600, whiteSpace: 'nowrap', minWidth: '200px' }}>
                                    % Conciliado no geral
                                </span>
                                <BarPercent value={Number(stats.porcentagemGeral)} color="#0d6efd" />
                                <span style={{ fontSize: '14px', fontWeight: 700, color: '#0d6efd', minWidth: '50px', textAlign: 'right' }}>
                                    {Number(stats.porcentagemGeral).toFixed(1)}%
                                </span>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
}

// ─── AGE da Conta ────────────────────────────────────────────────────────────

const FAIXAS_ORDER = [
    'A vencer', '0 a 30 dias', '31 a 60 dias', '61 a 90 dias',
    '91 a 120 dias', '121 a 180 dias', 'Acima de 180 dias',
];

function AgeSaldoSection({ age }: { age: { totalGeral: number; faixas: { faixa: string; total: number }[] } | null }) {
    const color = '#5c3d91';

    if (!age) {
        return (
            <div style={{ marginBottom: '32px' }}>
                <div style={{ background: color, color: '#fff', borderRadius: '6px 6px 0 0', padding: '10px 16px', fontWeight: 700, fontSize: '14px' }}>
                    AGE DA CONTA (IDADE DO SALDO)
                </div>
                <div style={{ border: `1px solid ${color}`, borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '24px', textAlign: 'center' }}>
                    <span className="spinner-border spinner-border-sm" style={{ color }} role="status" />
                </div>
            </div>
        );
    }

    const faixasMap = Object.fromEntries(age.faixas.map(f => [f.faixa, f.total]));
    const total = age.totalGeral;

    return (
        <div style={{ marginBottom: '32px' }}>
            <div style={{
                background: color, color: '#fff', borderRadius: '6px 6px 0 0',
                padding: '10px 16px', fontWeight: 700, fontSize: '14px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
                <span>AGE DA CONTA (IDADE DO SALDO)</span>
                <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '12px', padding: '2px 12px', fontSize: '13px', fontWeight: 700 }}>
                    {BRL.format(total)}
                </span>
            </div>
            <div style={{ border: `1px solid ${color}`, borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#fff', overflowX: 'auto' }}>
                <table className="table table-sm mb-0" style={{ fontSize: '13px' }}>
                    <thead style={{ background: '#f3f0f8' }}>
                        <tr>
                            <th style={{ padding: '8px 14px', fontWeight: 600, color: color }}>Faixa</th>
                            <th style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 600, color: color }}>Valor</th>
                            <th style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 600, color: color, width: '80px' }}>%</th>
                            <th style={{ padding: '8px 14px', width: '180px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {FAIXAS_ORDER.map(faixa => {
                            const valor = faixasMap[faixa] ?? 0;
                            const pct = total > 0 ? (valor / total) * 100 : 0;
                            const barWidth = Math.min(100, pct);
                            return (
                                <tr key={faixa} style={{ borderBottom: '1px solid #f0eaf8' }}>
                                    <td style={{ padding: '8px 14px', color: '#495057' }}>{faixa}</td>
                                    <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {BRL.format(valor)}
                                    </td>
                                    <td style={{ padding: '8px 14px', textAlign: 'right', color: '#6c757d', whiteSpace: 'nowrap' }}>
                                        {pct.toFixed(1)}%
                                    </td>
                                    <td style={{ padding: '8px 14px' }}>
                                        <div style={{ background: '#e9ecef', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                                            <div style={{ width: `${barWidth}%`, height: '100%', background: faixa === 'A vencer' ? '#198754' : color, borderRadius: '4px', transition: 'width 0.3s' }} />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr style={{ background: '#f3f0f8', fontWeight: 700 }}>
                            <td style={{ padding: '8px 14px', color: color }}>Total Geral</td>
                            <td style={{ padding: '8px 14px', textAlign: 'right', color: color, whiteSpace: 'nowrap' }}>{BRL.format(total)}</td>
                            <td style={{ padding: '8px 14px', textAlign: 'right', color: '#6c757d' }}>100,0%</td>
                            <td />
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}

export default CapaConciliacao;
